/**
 * Transaction List Page Component
 * Redesigned with modern UI components from shadcn
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-4
 *
 * @description
 * Features:
 * - Animated stat cards with counters
 * - Enhanced status badges with pulse animation
 * - Modern filter UI with better UX
 * - Hover effects and transitions
 * - Context-sensitive actions based on status
 * - URL-based filter state (shareable links)
 * - Pagination
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus,
  Download,
  X,
  ClipboardCheck,
  CheckCircle,
  CreditCard,
  Factory,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Search,
  Package,
  Scale,
  Clock,
  CheckCircle2,
  Filter,
  RefreshCw,
  Truck,
  Edit,
  CheckSquare,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { MultiSelect } from '@/components/ui/multi-select';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import { useTransactions, useTransactionSummary, useApproveDriverCollection } from '@/hooks/useTransactions';
import { useTransactionFilters } from '@/hooks/useTransactionFilters';
import { searchGaushalaIds } from '@/services/manufacturing/manufacturingApi';
import { useMasterData } from '@/hooks/useMasterData';
import { TransactionStatusBadge, TransactionStatus } from '@/components/manufacturing/badges/TransactionStatusBadge';
import { QCGradeBadge } from '@/components/manufacturing/badges/QCGradeBadge';
import { QCVerificationForm } from '@/components/manufacturing/forms/QCVerificationForm';
import { TransactionApprovalForm } from '@/components/manufacturing/forms/TransactionApprovalForm';
import { NewTransactionWizard } from '@/components/manufacturing/forms/NewTransactionWizard';
import { TransactionDetailModal } from '@/components/manufacturing/modals/TransactionDetailModal';
import { StatCard } from '@/components/manufacturing/cards/StatCard';
import { exportToCsv, formatDateForExport, formatNumberForExport } from '@/lib/export';
import { TransactionDTO } from '@/services/manufacturing/manufacturingApi';
import { cn } from '@/lib/utils';
import { API_ENDPOINTS } from '@/lib/apiConfig';

/**
 * Status options for multi-select
 */
const STATUS_OPTIONS = [
  { value: 'UP_COMING', label: 'Pending Approval' },
  { value: 'INITIALIZED', label: 'Initialized' },
  { value: 'QC_PENDING', label: 'Pending QC' },
  { value: 'QC_VERIFIED', label: 'QC Verified' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'CONSUMED', label: 'Consumed' },
];

/**
 * Grade options
 */
const GRADE_OPTIONS = ['A', 'B', 'C', 'D'];

/**
 * Format date for display
 */
function formatDateTime(dateString: string): string {
  try {
    return format(new Date(dateString), 'PP');
  } catch {
    return dateString;
  }
}

/**
 * Format quantity with KG suffix
 */
function formatQuantity(quantity: number): string {
}

/**
 * Fetch gaushala name from gaushala-service API
 */
function useGaushalaName(gaushalaId: number | null) {
  return useQuery<string>({
    queryKey: ['gaushala-name', gaushalaId],
    queryFn: async () => {
      if (!gaushalaId) return 'Unknown';
      try {
        const response = await fetch(
          API_ENDPOINTS.gaushala.getGaushalaName(gaushalaId)
        );
        if (!response.ok) return `Gaushala ${gaushalaId}`;
        const data = await response.json();
        return data.data || `Gaushala ${gaushalaId}`;
      } catch (error) {
        console.warn(`Failed to fetch gaushala name for ID ${gaushalaId}:`, error);
        return `Gaushala ${gaushalaId}`;
      }
    },
    enabled: !!gaushalaId,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

/**
 * Gaushala Name Display Component
 */
interface GaushalaNameCellProps {
  gaushalaId: number | null;
}

function GaushalaNameCell({ gaushalaId }: GaushalaNameCellProps) {
  const { data: gaushalaName, isLoading } = useGaushalaName(gaushalaId);

  if (isLoading) {
    return <Skeleton className="h-4 w-24" />;
  }

  return <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{gaushalaName}</span>;
}

/**
 * Transaction List Page Component
 */
export function TransactionListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { id: detailTransactionId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, setFilters, clearFilters, hasFilters } = useTransactionFilters();
  const [qcTransaction, setQcTransaction] = useState<TransactionDTO | null>(null);
  const [approvalTransaction, setApprovalTransaction] = useState<TransactionDTO | null>(null);
  const [isNewTransactionOpen, setIsNewTransactionOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [detailTransactionIdNumber, setDetailTransactionIdNumber] = useState<number | null>(null);

  // Approve driver collection mutation
  const approveCollectionMutation = useApproveDriverCollection();

  // Open new transaction wizard when navigating to /new
  useEffect(() => {
    if (location.pathname.endsWith('/new')) {
      setIsNewTransactionOpen(true);
    }
  }, [location.pathname]);

  // Handle detail view when ID is in URL
  useEffect(() => {
    if (detailTransactionId && !location.pathname.endsWith('/new')) {
      const numId = parseInt(detailTransactionId, 10);
      if (!isNaN(numId)) {
        setDetailTransactionIdNumber(numId);
      }
    }
  }, [detailTransactionId, location.pathname]);

  // Handle wizard close - navigate back to list
  const handleNewTransactionClose = (open: boolean) => {
    setIsNewTransactionOpen(open);
    if (!open) {
      navigate('/manufacturing/transactions');
    }
  };

  // Pagination from URL
  const page = parseInt(searchParams.get('page') || '0', 10);
  const size = parseInt(searchParams.get('size') || '10', 10);

  // Fetch master data
  const { rawMaterialTypes: materialTypes, isLoading: isLoadingMaterialTypes } = useMasterData();

  // Resolve gaushala search to IDs on frontend (bypasses broken backend-to-backend call)
  const [resolvedGaushalaIds, setResolvedGaushalaIds] = useState<number[] | undefined>(undefined);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const gaushalaDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (gaushalaDebounceRef.current) {
      clearTimeout(gaushalaDebounceRef.current);
    }
    const searchTerm = (filters.gaushalaSearch || '').trim();
    if (!searchTerm) {
      setResolvedGaushalaIds(undefined);
      return;
    }
    gaushalaDebounceRef.current = setTimeout(() => {
      searchGaushalaIds(searchTerm).then((ids) => {
        setResolvedGaushalaIds(ids.length > 0 ? ids : [-1]); // -1 = no match, returns 0 results
      });
    }, 400);
    return () => {
      if (gaushalaDebounceRef.current) {
        clearTimeout(gaushalaDebounceRef.current);
      }
    };
  }, [filters.gaushalaSearch]);

  // Prepare filters for API - sends gaushalaIds instead of gaushalaSearch
  const apiFilters = useMemo(() => {
    return {
      status: filters.status.length > 0 ? filters.status : undefined,
      gaushalaIds: resolvedGaushalaIds,
      grade: filters.grade || undefined,
      materialTypeId: filters.materialTypeId || undefined,
      fromDate: filters.fromDate || undefined,
      toDate: filters.toDate || undefined,
      page,
      size,
    };
  }, [filters.status, resolvedGaushalaIds, filters.grade, filters.materialTypeId, filters.fromDate, filters.toDate, page, size]);

  // Fetch transactions
  const { data, isLoading, error, refetch } = useTransactions(apiFilters);

  // Fetch transaction summary metrics from backend (all-time, across all pages)
  const { data: summaryData } = useTransactionSummary();

  // Stats from backend summary (accurate across all transactions, not just current page)
  const stats = useMemo(() => {
    if (!summaryData) {
      return {
        total: data?.totalElements || 0,
        totalQuantity: 0,
        pendingQc: 0,
        approved: 0,
      };
    }

    return {
      total: summaryData.total,
      totalQuantity: summaryData.totalQuantityKg,
      pendingQc: summaryData.pendingQc,
      approved: summaryData.approved,
    };
  }, [summaryData, data?.totalElements]);

  /**
   * Handle page change
   */
  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage.toString());
    setSearchParams(newParams);
  };

  /**
   * Handle page size change
   */
  const handlePageSizeChange = (newSize: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('size', newSize.toString());
    newParams.set('page', '0'); // Reset to first page when changing page size
    setSearchParams(newParams);
  };

  /**
   * Handle export to CSV
   */
  const handleExport = () => {
    if (!data || data.content.length === 0) {
      return;
    }

    const columns = [
      { key: 'batchCode', label: 'Batch Code' },
      { key: 'gaushalaName', label: 'Gaushala' },
      { key: 'rawMaterialTypeName', label: 'Material Type' },
      { key: 'quantityKg', label: 'Quantity (KG)' },
      { key: 'grade', label: 'Grade' },
      { key: 'status', label: 'Status' },
      { key: 'createdAt', label: 'Created At' },
    ];

    const exportData = data.content.map((transaction) => ({
      batchCode: transaction.batchCode,
      gaushalaName: transaction.gaushala?.name || transaction.gaushalaName || '',
      rawMaterialTypeName: transaction.rawMaterialType || transaction.rawMaterialTypeName || '',
      quantityKg: formatNumberForExport(transaction.quantityKg),
      grade: transaction.qc?.grade || '',
      status: transaction.status,
      createdAt: formatDateForExport(transaction.createdAt),
    }));

    const filename = `transactions-${format(new Date(), 'yyyy-MM-dd')}`;
    exportToCsv(exportData, filename, columns);
  };

  /**
   * Handle approve driver collection
   */
  const handleApproveCollection = async (transaction: TransactionDTO) => {
    try {
      await approveCollectionMutation.mutateAsync(transaction.id);
      toast.success('Driver collection approved successfully', {
        description: `Transaction ${transaction.batchCode} moved to QC queue`,
      });
      refetch();
    } catch (error: any) {
      toast.error('Failed to approve collection', {
        description: error.message || 'Please try again',
      });
    }
  };

  /**
   * Handle row action based on status
   */
  const handleAction = (transaction: TransactionDTO, action: string) => {
    switch (action) {
      case 'edit':
        // Navigate to edit mode with transaction ID
        navigate(`/manufacturing/transactions/new?id=${transaction.id}`);
        break;
      case 'approve-collection':
        // Call approve collection API
        handleApproveCollection(transaction);
        break;
      case 'verify-qc':
        setQcTransaction(transaction);
        break;
      case 'approve':
        setApprovalTransaction(transaction);
        break;
      case 'view-payment':
        navigate(`/manufacturing/payments?transactionId=${transaction.id}`);
        break;
      case 'use-in-batch':
        navigate(`/manufacturing/production/new?transactionId=${transaction.id}`);
        break;
      case 'view-details':
        navigate(`/manufacturing/transactions/${transaction.id}`);
        break;
    }
  };

  /**
   * Get context-sensitive actions for a transaction
   */
  const getActionsForTransaction = (transaction: TransactionDTO) => {
    const actions = [];

    switch (transaction.status) {
      case 'UP_COMING':
        // Driver-initiated transactions awaiting manufacturing approval
        actions.push({ label: 'Edit Transaction', value: 'edit', icon: Edit });
        actions.push({ label: 'Approve Collection', value: 'approve-collection', icon: CheckSquare });
        break;
      case 'INITIALIZED':
        actions.push({ label: 'Submit for QC', value: 'verify-qc', icon: ClipboardCheck });
        break;
      case 'QC_PENDING':
        actions.push({ label: 'Verify QC', value: 'verify-qc', icon: ClipboardCheck });
        break;
      case 'QC_VERIFIED':
        actions.push({ label: 'Approve', value: 'approve', icon: CheckCircle });
        break;
      case 'APPROVED':
        actions.push({ label: 'View Payment', value: 'view-payment', icon: CreditCard });
        break;
      case 'READY_FOR_PRODUCTION':
        actions.push({ label: 'Use in Batch', value: 'use-in-batch', icon: Factory });
        break;
    }

    // All statuses have view details
    actions.push({ label: 'View Details', value: 'view-details', icon: null });

    return actions;
  };

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      // refetchQueries invalidates AND refetches active queries in one pass,
      // avoiding the double-fetch caused by invalidateQueries + refetch().
      // Includes gaushala-name so cached names (1h staleTime) also refresh.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['transactions'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['transaction-summary'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['gaushala-name'], type: 'active' }),
      ]);

      toast.success('Transactions refreshed successfully', {
        description: 'All transaction data has been updated.',
      });
    } catch (err) {
      console.error('[TransactionListPage] Refresh failed:', err);
      toast.error('Failed to refresh transactions', {
        description: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, queryClient]);

  return (
    <div className="space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Raw Material Transactions</h1>
          <p className="text-muted-foreground mt-1">
            Manage incoming raw materials from gaushalas
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="shrink-0"
            title="Refresh transactions"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={!data || data.content.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => navigate('/manufacturing/transactions/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Transaction
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Transactions"
          value={stats.total}
          icon={Package}
          variant="info"
          description="All time records"
        />
        <StatCard
          title="Total Quantity"
          value={stats.totalQuantity}
          suffix="KG"
          decimals={2}
          icon={Scale}
          variant="success"
          description="Raw material collected"
        />
        <StatCard
          title="Pending QC"
          value={stats.pendingQc}
          icon={Clock}
          variant="warning"
          description="Awaiting verification"
        />
        <StatCard
          title="Approved"
          value={stats.approved}
          icon={CheckCircle2}
          variant="success"
          description="Ready for production"
        />
      </div>

      {/* Filters Card - Enhanced with better UX */}
      <Collapsible open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
        <Card className={hasFilters ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20' : ''}>
          <CardHeader className="py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <CardTitle className="text-base font-semibold">Advanced Filters</CardTitle>
                {hasFilters && (
                  <Badge variant="default" className="ml-2 bg-blue-600 hover:bg-blue-700">
                    {/* Count active filters */}
                    {[
                      filters.status.length > 0 ? 1 : 0,
                      filters.gaushalaSearch ? 1 : 0,
                      filters.grade ? 1 : 0,
                      filters.materialTypeId ? 1 : 0,
                      filters.from ? 1 : 0,
                      filters.to ? 1 : 0,
                    ].reduce((a, b) => a + b, 0)} Active
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>
                )}
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm">
                    {isFiltersOpen ? 'Hide' : 'Show'} Filters
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>

            {/* Display active filters as chips below header */}
            {hasFilters && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                {filters.status.length > 0 && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <span className="font-medium">Status:</span>
                    {filters.status.map(s => {
                      const label = STATUS_OPTIONS.find(opt => opt.value === s)?.label || s;
                      return (
                        <span key={s} className="text-xs">
                          {label}
                          <button
                            onClick={() => setFilters({
                              status: filters.status.filter(f => f !== s)
                            })}
                            className="ml-1 hover:text-white"
                          >
                            ×
                          </button>
                        </span>
                      );
                    }).reduce((acc, el, i) => (
                      acc.length === 0 ? [el] : [...acc, <span key={`sep-${i}`}>, </span>, el]
                    ), [])}
                  </Badge>
                )}
                {filters.gaushalaSearch && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <span className="font-medium">Gaushala:</span>
                    <span>{filters.gaushalaSearch}</span>
                    <button
                      onClick={() => setFilters({ gaushalaSearch: '' })}
                      className="ml-1 hover:text-white"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {filters.grade && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <span className="font-medium">Grade:</span>
                    <span>Grade {filters.grade}</span>
                    <button
                      onClick={() => setFilters({ grade: '' })}
                      className="ml-1 hover:text-white"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {filters.materialTypeId && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <span className="font-medium">Material:</span>
                    <span>{materialTypes?.find((m: any) => m.id.toString() === filters.materialTypeId)?.name || filters.materialTypeId}</span>
                    <button
                      onClick={() => setFilters({ materialTypeId: '' })}
                      className="ml-1 hover:text-white"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {(filters.fromDate || filters.toDate) && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <span className="font-medium">Date:</span>
                    <span>{filters.fromDate && format(new Date(filters.fromDate), 'MMM dd')} → {filters.toDate && format(new Date(filters.toDate), 'MMM dd')}</span>
                    <button
                      onClick={() => setFilters({ fromDate: '', toDate: '' })}
                      className="ml-1 hover:text-white"
                    >
                      ×
                    </button>
                  </Badge>
                )}
              </div>
            )}
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-4">
              <div className="space-y-4">
                {/* First Row - Status and Gaushala */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Status Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Transaction Status
                    </label>
                    <MultiSelect
                      options={STATUS_OPTIONS}
                      value={filters.status}
                      onChange={(value) => setFilters({ status: value })}
                      placeholder="Select transaction statuses..."
                    />
                    <p className="text-xs text-muted-foreground">Select one or more statuses to filter</p>
                  </div>

                  {/* Gaushala Search */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Gaushala Name
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by gaushala name..."
                        value={filters.gaushalaSearch}
                        onChange={(e) => setFilters({ gaushalaSearch: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Partial search supported</p>
                  </div>
                </div>

                {/* Second Row - Grade and Material Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Grade Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Quality Grade
                    </label>
                    <Select
                      value={filters.grade || 'all'}
                      onValueChange={(value) => setFilters({ grade: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select quality grade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Grades</SelectItem>
                        {GRADE_OPTIONS.map((grade) => (
                          <SelectItem key={grade} value={grade}>
                            Grade {grade}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Filter by QC grade (A, B, C, D)</p>
                  </div>

                  {/* Material Type Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Raw Material Type
                    </label>
                    <Select
                      value={filters.materialTypeId || 'all'}
                      onValueChange={(value) => setFilters({ materialTypeId: value === 'all' ? '' : value })}
                      disabled={isLoadingMaterialTypes}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={isLoadingMaterialTypes ? 'Loading materials...' : 'Select material type'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {materialTypes?.map((type: any) => (
                          <SelectItem key={type.id} value={type.id.toString()}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Filter by material type</p>
                  </div>
                </div>

                {/* Third Row - Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Date From */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      From Date
                    </label>
                    <Input
                      type="date"
                      value={filters.fromDate}
                      onChange={(e) => setFilters({ fromDate: e.target.value })}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">Start date (inclusive)</p>
                  </div>

                  {/* Date To */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      To Date
                    </label>
                    <Input
                      type="date"
                      value={filters.toDate}
                      onChange={(e) => setFilters({ toDate: e.target.value })}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">End date (inclusive)</p>
                  </div>
                </div>

                {/* Filter Actions - Bottom */}
                <div className="flex justify-between items-center pt-4 border-t">
                  <p className="text-xs text-muted-foreground">
                    Filters are applied automatically. Adjust any filter to update results.
                  </p>
                  {hasFilters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Reset All Filters
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Data Table */}
      <div className="bg-white dark:bg-gray-950 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <p className="text-destructive font-medium">Error loading transactions</p>
            <p className="text-sm text-muted-foreground mt-1">Please try again later</p>
            <Button variant="outline" className="mt-4" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Retrying...' : 'Retry'}
            </Button>
          </div>
        ) : !data || data.content.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground/50" />
            <p className="text-muted-foreground mt-4">No transactions found</p>
            <Button
              variant="link"
              onClick={() => navigate('/manufacturing/transactions/new')}
              className="mt-2"
            >
              Create your first transaction
            </Button>
          </div>
        ) : (
          <>
            {/* Table with dynamic height - uses calc to fill available viewport space */}
            <div className="overflow-x-auto">
              <div className="min-h-[400px] max-h-[calc(100vh-400px)] overflow-y-auto">
                <table className="w-full min-w-[1000px]">
                  <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Batch Code
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Gaushala
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Material
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Qty (KG)
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Available (KG)
                      </th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Grade
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-950 divide-y divide-gray-200 dark:divide-gray-700">
                    {data.content.map((transaction) => (
                      <tr
                        key={transaction.id}
                        className="group hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                      >
                        <td className="px-3 py-3 whitespace-nowrap">
                          <button
                            className="font-medium text-primary hover:text-primary/80 hover:underline text-sm"
                            onClick={() => navigate(`/manufacturing/transactions/${transaction.id}`)}
                          >
                            {transaction.batchCode}
                          </button>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                            <GaushalaNameCell gaushalaId={transaction.gaushala?.id || null} />
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <Badge variant="outline" className="font-normal text-xs">
                            {transaction.rawMaterialType || transaction.rawMaterialTypeName || '—'}
                          </Badge>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-right font-mono font-medium text-gray-900 dark:text-gray-100 text-sm">
                          {transaction.quantityKg.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-right font-mono font-medium text-blue-600 dark:text-blue-400 text-sm">
                          {transaction.quantityAvailableKg.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-center">
                          {transaction.qc?.grade ? (
                            <QCGradeBadge grade={transaction.qc.grade as any} size="sm" />
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <TransactionStatusBadge
                            status={transaction.status as TransactionStatus}
                            size="sm"
                            showPulse={transaction.status === 'INITIALIZED'}
                          />
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400 text-sm">
                          {formatDateTime(transaction.createdAt)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {getActionsForTransaction(transaction).map((action, index) => (
                                <React.Fragment key={action.value}>
                                  {index === getActionsForTransaction(transaction).length - 1 &&
                                    getActionsForTransaction(transaction).length > 1 && (
                                    <DropdownMenuSeparator />
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => handleAction(transaction, action.value)}
                                  >
                                    {action.icon && <action.icon className="h-4 w-4 mr-2" />}
                                    {action.label}
                                  </DropdownMenuItem>
                                </React.Fragment>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
              {/* Page size selector */}
              <div className="flex items-center space-x-2 mb-3 sm:mb-0">
                <span className="text-sm text-gray-700 dark:text-gray-300">Show</span>
                <select
                  value={size}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  entries (Total: {data.totalElements})
                </span>
              </div>

              {/* Page navigation */}
              <div className="flex items-center space-x-2">
                {/* Previous button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>

                {/* Page numbers */}
                <div className="hidden sm:flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => {
                    let pageNum;
                    if (data.totalPages <= 5) {
                      pageNum = i;
                    } else if (page < 3) {
                      pageNum = i;
                    } else if (page > data.totalPages - 4) {
                      pageNum = data.totalPages - 5 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="min-w-[32px]"
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}
                </div>

                {/* Next button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= data.totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              {/* Page info */}
              <div className="text-sm text-gray-700 dark:text-gray-300 mt-3 sm:mt-0">
                Page {page + 1} of {data.totalPages}
              </div>
            </div>
          </>
        )}
      </div>

      {/* QC Verification Form Dialog */}
      <QCVerificationForm
        transaction={qcTransaction}
        open={qcTransaction !== null}
        onOpenChange={(open) => {
          if (!open) setQcTransaction(null);
        }}
        onSuccess={() => {
          setQcTransaction(null);
          refetch();
        }}
      />

      {/* Transaction Approval Form Dialog */}
      <TransactionApprovalForm
        transaction={approvalTransaction}
        open={approvalTransaction !== null}
        onOpenChange={(open) => {
          if (!open) setApprovalTransaction(null);
        }}
        onSuccess={() => {
          setApprovalTransaction(null);
          refetch();
        }}
      />

      {/* New Transaction Wizard Dialog */}
      <NewTransactionWizard
        open={isNewTransactionOpen}
        onOpenChange={handleNewTransactionClose}
        onSuccess={() => {
          // Refresh the transaction list after successful creation
          refetch();
          navigate('/manufacturing/transactions');
        }}
      />

      {/* Transaction Detail Modal */}
      <TransactionDetailModal
        open={detailTransactionIdNumber !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailTransactionIdNumber(null);
            navigate('/manufacturing/transactions');
          }
        }}
        transactionId={detailTransactionIdNumber}
      />
    </div>
  );
}

export default TransactionListPage;
